import { OrganizationBrandingDTO } from '@reposit/api-client';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';
import { Header4, P2, P3 } from '../../components/Typography';
import { previewOrganizationBrandingEmailRequested } from '../../redux/organization-branding/organization-branding.actions';
import {
  OrganizationBrandingLogoContentType,
  UploadedOrganizationBrandingLogo,
} from '../../redux/organization-branding/organization-branding.types';

interface OrganizationBrandingFormValues {
  friendlyOrganizationName: string;
  emailFooterText: string;
}

interface EmailPreviewProps {
  error: string;
  isLoading: boolean;
  isReady: boolean;
  preview?: {
    fromName: string;
    html: string;
    subject: string;
  };
  requirements: PreviewRequirement[];
}

interface OrganizationBrandingEmailPreviewProps {
  activeLogoFileError: string;
  emailPreview?: {
    fromName: string;
    html: string;
    subject: string;
  };
  emailPreviewError: string;
  hasFetched: boolean;
  isPreviewingEmail: boolean;
  isUploadingLogo: boolean;
  organizationId: string;
  profile?: Pick<OrganizationBrandingDTO, 'emailHeaderLogoContentType' | 'emailHeaderLogoKey'> | null;
  uploadedLogo?: Pick<UploadedOrganizationBrandingLogo, 'emailHeaderLogoContentType' | 'emailHeaderLogoKey'>;
  uploadedLogoError: string;
  values: OrganizationBrandingFormValues;
}

interface PreviewRequirement {
  isComplete: boolean;
  label: string;
}

const EmailPreviewPanel = styled.div`
  background: ${(props) => props.theme.colors.bgAccent};
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: 640px;
  margin: 0;
  overflow: hidden;

  @media (max-width: 1000px) {
    height: 620px;
  }

  @media (max-width: 576px) {
    height: 560px;
  }
`;

const EmailPreviewHeader = styled.div`
  background: #fff;
  border-bottom: 1px solid rgba(58, 49, 94, 0.1);
  padding: 14px 16px;
`;

const EmailPreviewMeta = styled(P3)`
  margin: 2px 0 0;
  overflow-wrap: break-word;
`;

const EmailPreviewMetaBlock = styled.div`
  min-height: 52px;
`;

const EmailPreviewNotice = styled(P3)`
  color: ${(props) => props.theme.colors.body2};
  font-weight: 600;
  margin: 4px 0 0;
  overflow-wrap: break-word;

  &.error {
    color: ${(props) => props.theme.colors.warning};
  }
`;

const EmailPreviewFrameWrap = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 0;
  padding: 14px;
`;

const EmailPreviewIframe = styled.iframe`
  background: #fff;
  border: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: block;
  height: 100%;
  max-width: 640px;
  width: 100%;
`;

const EmailPreviewPlaceholder = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  margin: 0;
  padding: 24px 20px;
  text-align: center;
  width: 100%;
`;

const PlaceholderText = styled(P2)`
  margin: 0 0 14px;
  max-width: 360px;
`;

const PreviewChecklist = styled.ul`
  background: #fff;
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 8px;
  display: grid;
  gap: 8px;
  list-style: none;
  margin: 0;
  max-width: 360px;
  padding: 12px;
  width: 100%;
`;

const PreviewChecklistItem = styled.li`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const RequirementLabel = styled(P3)`
  color: ${(props) => props.theme.colors.body};
  font-weight: 600;
  margin: 0;
`;

const RequirementState = styled(P3)`
  background: rgba(255, 90, 31, 0.1);
  border-radius: 999px;
  color: ${(props) => props.theme.colors.warning};
  font-weight: 700;
  margin: 0;
  padding: 3px 8px;

  &.complete {
    background: rgba(64, 201, 162, 0.12);
    color: ${(props) => props.theme.colors.positive};
  }
`;

type PreviewStatusVariant = 'previewNotice' | 'placeholderMessage' | 'headerFallback';

const getPreviewStatusMessage = (
  error: string,
  isLoading: boolean,
  isReady: boolean,
  variant: PreviewStatusVariant,
  hasVisiblePreview = false,
): string | undefined => {
  if (error) {
    return variant === 'headerFallback' ? 'Preview needs attention.' : error;
  }

  if (isLoading) {
    if (variant === 'previewNotice') {
      return hasVisiblePreview ? 'Rendering an updated preview...' : 'Rendering email preview...';
    }

    return variant === 'headerFallback' ? 'Rendering preview.' : 'Rendering email preview...';
  }

  if (!isReady) {
    if (variant === 'previewNotice') {
      return undefined;
    }

    return variant === 'headerFallback'
      ? 'Waiting for branding fields and logo.'
      : 'Complete the branding fields and logo to preview the email.';
  }

  return variant === 'previewNotice' ? undefined : 'Rendering email preview...';
};

const EmailPreview: React.FC<EmailPreviewProps> = ({ error, isLoading, isReady, preview, requirements }) => {
  const shouldShowPreview = !!preview && (isReady || isLoading || !!error);
  const visiblePreview = shouldShowPreview ? preview : undefined;
  const previewNotice = getPreviewStatusMessage(error, isLoading, isReady, 'previewNotice', !!visiblePreview);
  const placeholderMessage = getPreviewStatusMessage(error, isLoading, isReady, 'placeholderMessage');
  const headerFallback = getPreviewStatusMessage(error, isLoading, isReady, 'headerFallback');
  const shouldShowChecklist = !error && !isLoading && !isReady && !visiblePreview;

  return (
    <EmailPreviewPanel>
      <EmailPreviewHeader>
        <Header4 style={{ marginBottom: 2 }}>Sample email preview</Header4>
        <EmailPreviewMetaBlock>
          {visiblePreview ? (
            <>
              <EmailPreviewMeta>Subject: {visiblePreview.subject}</EmailPreviewMeta>
              <EmailPreviewMeta>From: {visiblePreview.fromName}</EmailPreviewMeta>
              {previewNotice ? (
                <EmailPreviewNotice className={error ? 'error' : undefined}>{previewNotice}</EmailPreviewNotice>
              ) : null}
            </>
          ) : (
            <EmailPreviewNotice className={error ? 'error' : undefined}>{previewNotice || headerFallback}</EmailPreviewNotice>
          )}
        </EmailPreviewMetaBlock>
      </EmailPreviewHeader>
      <EmailPreviewFrameWrap>
        {visiblePreview ? (
          <EmailPreviewIframe
            title="Organization branding email preview"
            srcDoc={visiblePreview.html}
            sandbox="allow-same-origin"
            scrolling="auto"
          />
        ) : (
          <EmailPreviewPlaceholder>
            <PlaceholderText>{placeholderMessage}</PlaceholderText>
            {shouldShowChecklist ? (
              <PreviewChecklist>
                {requirements.map((requirement) => (
                  <PreviewChecklistItem key={requirement.label}>
                    <RequirementLabel>{requirement.label}</RequirementLabel>
                    <RequirementState className={requirement.isComplete ? 'complete' : undefined}>
                      {requirement.isComplete ? 'Ready' : 'Needed'}
                    </RequirementState>
                  </PreviewChecklistItem>
                ))}
              </PreviewChecklist>
            ) : null}
          </EmailPreviewPlaceholder>
        )}
      </EmailPreviewFrameWrap>
    </EmailPreviewPanel>
  );
};

const OrganizationBrandingEmailPreview: React.FC<OrganizationBrandingEmailPreviewProps> = ({
  activeLogoFileError,
  emailPreview,
  emailPreviewError,
  hasFetched,
  isPreviewingEmail,
  isUploadingLogo,
  organizationId,
  profile,
  uploadedLogo,
  uploadedLogoError,
  values,
}) => {
  const dispatch = useDispatch();
  const effectiveEmailHeaderLogoKey = uploadedLogo ? uploadedLogo.emailHeaderLogoKey : profile ? profile.emailHeaderLogoKey : '';
  const effectiveEmailHeaderLogoContentType: OrganizationBrandingLogoContentType | undefined = uploadedLogo
    ? uploadedLogo.emailHeaderLogoContentType
    : profile
      ? profile.emailHeaderLogoContentType
      : undefined;
  const normalizedFriendlyName = values.friendlyOrganizationName.trim();
  const normalizedFooterText = values.emailFooterText.trim();
  const previewInput = useMemo(
    () => ({
      friendlyOrganizationName: normalizedFriendlyName,
      emailFooterText: normalizedFooterText,
      emailHeaderLogoKey: effectiveEmailHeaderLogoKey,
      emailHeaderLogoContentType: effectiveEmailHeaderLogoContentType,
    }),
    [normalizedFriendlyName, normalizedFooterText, effectiveEmailHeaderLogoKey, effectiveEmailHeaderLogoContentType],
  );
  const [debouncedPreviewInput] = useDebounce(previewInput, 500);
  const previewBlocker = activeLogoFileError || uploadedLogoError || '';
  const previewRequirements = [
    { label: 'Sender name', isComplete: !!normalizedFriendlyName },
    { label: 'Logo', isComplete: !!effectiveEmailHeaderLogoKey && !!effectiveEmailHeaderLogoContentType && !previewBlocker },
  ];
  const isEmailPreviewReady =
    !!debouncedPreviewInput.friendlyOrganizationName &&
    !!debouncedPreviewInput.emailHeaderLogoKey &&
    !!debouncedPreviewInput.emailHeaderLogoContentType &&
    !previewBlocker &&
    !isUploadingLogo;

  useEffect(() => {
    if (!hasFetched || !isEmailPreviewReady || !debouncedPreviewInput.emailHeaderLogoContentType) {
      return;
    }

    dispatch(
      previewOrganizationBrandingEmailRequested({
        organizationId,
        friendlyOrganizationName: debouncedPreviewInput.friendlyOrganizationName,
        emailFooterText: debouncedPreviewInput.emailFooterText,
        emailHeaderLogoKey: debouncedPreviewInput.emailHeaderLogoKey,
        emailHeaderLogoContentType: debouncedPreviewInput.emailHeaderLogoContentType,
      }),
    );
  }, [debouncedPreviewInput, dispatch, hasFetched, isEmailPreviewReady, organizationId]);

  return (
    <EmailPreview
      error={previewBlocker || emailPreviewError || ''}
      isLoading={isUploadingLogo || isPreviewingEmail}
      isReady={isEmailPreviewReady}
      preview={emailPreview}
      requirements={previewRequirements}
    />
  );
};

export default OrganizationBrandingEmailPreview;
